import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Search,
  TrendingUp,
  TrendingDown,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  Bitcoin,
  BarChart3,
  Coins,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import { MainLayout } from "@/components/layout/main-layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { Quote, Account, Instrument } from "@shared/schema";

function formatCurrency(value: number | string | null | undefined): string {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (num === null || num === undefined || isNaN(num)) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

function formatPercent(value: number | string | null | undefined): string {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (num === null || num === undefined || isNaN(num)) return "0.00%";
  return `${num >= 0 ? "+" : ""}${num.toFixed(2)}%`;
}

function QuoteCard({ quote, onSelect, assetClass }: { quote: Quote; onSelect: (symbol: string) => void; assetClass: string }) {
  const change = parseFloat(quote.change?.toString() || "0");
  const isPositive = change >= 0;

  return (
    <Card
      className="cursor-pointer hover-elevate"
      onClick={() => onSelect(quote.symbol)}
      data-testid={`quote-card-${quote.symbol}`}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className={`h-8 w-8 rounded-md flex items-center justify-center ${
              assetClass === "crypto" ? "bg-orange-500/10" : assetClass === "option" ? "bg-purple-500/10" : "bg-primary/10"
            }`}>
              {assetClass === "crypto" ? (
                <Bitcoin className="h-4 w-4 text-orange-500" />
              ) : assetClass === "option" ? (
                <BarChart3 className="h-4 w-4 text-purple-500" />
              ) : (
                <span className="text-xs font-bold text-primary">{quote.symbol?.slice(0, 2)}</span>
              )}
            </div>
            <div>
              <p className="font-medium">{quote.symbol}</p>
              <p className="text-xs text-muted-foreground truncate max-w-[100px]">{quote.name}</p>
            </div>
          </div>
          <Badge variant={isPositive ? "default" : "destructive"} className="text-xs">
            {isPositive ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
            {formatPercent(quote.changePercent)}
          </Badge>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xl font-bold">{formatCurrency(quote.lastPrice)}</span>
          <span className={`text-sm ${isPositive ? "text-chart-1" : "text-destructive"}`}>
            {isPositive ? "+" : ""}
            {formatCurrency(quote.change)}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function OptionsChain({ underlyingSymbol, underlyingPrice }: { underlyingSymbol: string; underlyingPrice: number }) {
  const { data: instruments } = useQuery<Instrument[]>({
    queryKey: ["/api/instruments"],
  });

  const optionsForSymbol = instruments?.filter(
    (i) => i.underlyingSymbol === underlyingSymbol
  ) || [];

  const calls = optionsForSymbol.filter((o) => o.optionType === "call");
  const puts = optionsForSymbol.filter((o) => o.optionType === "put");

  if (optionsForSymbol.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No options available for {underlyingSymbol}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="text-sm text-muted-foreground">
        Underlying: {underlyingSymbol} @ {formatCurrency(underlyingPrice)}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm text-chart-1">Calls</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Strike</TableHead>
                  <TableHead className="text-xs">Exp</TableHead>
                  <TableHead className="text-xs text-right">Bid</TableHead>
                  <TableHead className="text-xs text-right">Ask</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {calls.map((option) => (
                  <TableRow key={option.symbol} className="cursor-pointer hover:bg-muted/50" data-testid={`option-row-${option.symbol}`}>
                    <TableCell className="py-2 text-sm font-medium">${option.strikePrice}</TableCell>
                    <TableCell className="py-2 text-sm text-muted-foreground">{option.expirationDate}</TableCell>
                    <TableCell className="py-2 text-sm text-right">$2.15</TableCell>
                    <TableCell className="py-2 text-sm text-right">$2.25</TableCell>
                  </TableRow>
                ))}
                {calls.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-4 text-muted-foreground">No calls available</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm text-destructive">Puts</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Strike</TableHead>
                  <TableHead className="text-xs">Exp</TableHead>
                  <TableHead className="text-xs text-right">Bid</TableHead>
                  <TableHead className="text-xs text-right">Ask</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {puts.map((option) => (
                  <TableRow key={option.symbol} className="cursor-pointer hover:bg-muted/50" data-testid={`option-row-${option.symbol}`}>
                    <TableCell className="py-2 text-sm font-medium">${option.strikePrice}</TableCell>
                    <TableCell className="py-2 text-sm text-muted-foreground">{option.expirationDate}</TableCell>
                    <TableCell className="py-2 text-sm text-right">$1.85</TableCell>
                    <TableCell className="py-2 text-sm text-right">$1.95</TableCell>
                  </TableRow>
                ))}
                {puts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-4 text-muted-foreground">No puts available</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function CryptoOrderTicket({
  instruments,
  onOrder,
  isPending,
}: {
  instruments: Instrument[];
  onOrder: (data: { symbol: string; quantity: number; side: string; orderType: string }) => void;
  isPending: boolean;
}) {
  const [selectedCrypto, setSelectedCrypto] = useState("");
  const [amount, setAmount] = useState("");
  const [orderSide, setOrderSide] = useState<"buy" | "sell">("buy");

  const cryptoInstruments = instruments.filter((i) => i.assetClass === "crypto");
  const selectedInstrument = cryptoInstruments.find((i) => i.symbol === selectedCrypto);

  const cryptoPrices: Record<string, number> = {
    "BTC-USD": 97250.00,
    "ETH-USD": 3450.00,
    "SOL-USD": 145.50,
    "DOGE-USD": 0.082,
    "XRP-USD": 0.62,
  };

  const currentPrice = cryptoPrices[selectedCrypto] || 0;
  const estimatedValue = parseFloat(amount || "0") * currentPrice;

  const handleOrder = () => {
    if (!selectedCrypto || !amount) return;
    onOrder({
      symbol: selectedCrypto,
      quantity: parseFloat(amount),
      side: orderSide === "buy" ? "Buy" : "Sell",
      orderType: "Market",
    });
    setAmount("");
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Cryptocurrency</Label>
        <Select value={selectedCrypto} onValueChange={setSelectedCrypto}>
          <SelectTrigger data-testid="select-crypto">
            <SelectValue placeholder="Select crypto" />
          </SelectTrigger>
          <SelectContent>
            {cryptoInstruments.map((crypto) => (
              <SelectItem key={crypto.symbol} value={crypto.symbol}>
                <div className="flex items-center gap-2">
                  <Bitcoin className="h-4 w-4 text-orange-500" />
                  {crypto.symbol} - {crypto.name}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedInstrument && (
        <div className="p-3 rounded-md bg-orange-500/10 space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Current Price</span>
            <span className="font-bold text-orange-500">{formatCurrency(currentPrice)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">24h Change</span>
            <span className="text-chart-1">+2.45%</span>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label>Amount ({selectedInstrument?.baseCurrency || "Units"})</Label>
        <Input
          type="number"
          placeholder="0.00"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          step="0.0001"
          min="0"
          data-testid="input-crypto-amount"
        />
      </div>

      <Tabs value={orderSide} onValueChange={(v) => setOrderSide(v as "buy" | "sell")} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="buy">Buy</TabsTrigger>
          <TabsTrigger value="sell">Sell</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="space-y-2 pt-4 border-t">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Estimated {orderSide === "buy" ? "Cost" : "Value"}</span>
          <span className="font-bold">{formatCurrency(estimatedValue)}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Fee (0.5%)</span>
          <span>{formatCurrency(estimatedValue * 0.005)}</span>
        </div>
      </div>

      <Button
        className="w-full"
        variant={orderSide === "sell" ? "destructive" : "default"}
        onClick={handleOrder}
        disabled={isPending || !selectedCrypto || !amount}
        data-testid={`button-crypto-${orderSide}`}
      >
        {isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          `${orderSide === "buy" ? "Buy" : "Sell"} ${selectedInstrument?.baseCurrency || "Crypto"}`
        )}
      </Button>
    </div>
  );
}

interface BrokerageStatusData {
  alpaca: { configured: boolean; mode: string; features: string[] };
  tradier: { configured: boolean; mode: string; features: string[] };
  coinbase: { configured: boolean; mode: string; features: string[] };
  kraken: { configured: boolean; mode: string; features: string[] };
  interactiveBrokers: { configured: boolean; mode: string; features: string[] };
}

export default function TradingPage() {
  const { toast } = useToast();
  const [assetTab, setAssetTab] = useState("stocks");
  const [selectedSymbol, setSelectedSymbol] = useState("");
  const [quantity, setQuantity] = useState("");
  const [orderType, setOrderType] = useState("Market");
  const [limitPrice, setLimitPrice] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [optionsUnderlying, setOptionsUnderlying] = useState("AAPL");
  const [selectedBrokerage, setSelectedBrokerage] = useState("paper");

  const { data: quotes, isLoading: quotesLoading, refetch } = useQuery<Quote[]>({
    queryKey: ["/api/quotes"],
  });

  const { data: instruments } = useQuery<Instrument[]>({
    queryKey: ["/api/instruments"],
  });

  const { data: accounts } = useQuery<Account[]>({
    queryKey: ["/api/accounts"],
  });

  const { data: brokerageStatus } = useQuery<BrokerageStatusData>({
    queryKey: ["/api/brokerages/status"],
  });

  const alpacaConfigured = brokerageStatus?.alpaca?.configured;
  const tradierConfigured = brokerageStatus?.tradier?.configured;

  const placeOrderMutation = useMutation({
    mutationFn: async (data: {
      symbol: string;
      quantity: number;
      side: string;
      orderType: string;
      limitPrice?: number;
      brokerage?: string;
    }) => {
      if (data.brokerage === "alpaca") {
        const res = await apiRequest("POST", "/api/alpaca/orders", {
          symbol: data.symbol,
          qty: data.quantity,
          side: data.side.toLowerCase(),
          type: data.orderType.toLowerCase(),
          time_in_force: "day",
          limit_price: data.limitPrice,
        });
        return res.json();
      } else if (data.brokerage === "tradier") {
        const res = await apiRequest("POST", "/api/tradier/orders/equity", {
          symbol: data.symbol,
          quantity: data.quantity,
          side: data.side.toLowerCase(),
          type: data.orderType.toLowerCase(),
          duration: "day",
          price: data.limitPrice,
        });
        return res.json();
      } else {
        const res = await apiRequest("POST", "/api/orders", data);
        return res.json();
      }
    },
    onSuccess: () => {
      const brokerageLabel = selectedBrokerage === "paper" ? "simulated" : selectedBrokerage;
      toast({
        title: "Order Placed",
        description: `Your ${orderType.toLowerCase()} order has been submitted via ${brokerageLabel}.`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/orders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/positions"] });
      if (selectedBrokerage === "alpaca") {
        queryClient.invalidateQueries({ queryKey: ["/api/alpaca/positions"] });
      }
      setSelectedSymbol("");
      setQuantity("");
      setLimitPrice("");
    },
    onError: (error: any) => {
      toast({
        title: "Order Failed",
        description: error.message || "Unable to place order. Please try again.",
        variant: "destructive",
      });
    },
  });

  const selectedQuote = quotes?.find((q) => q.symbol === selectedSymbol);
  const estimatedCost = selectedQuote
    ? parseFloat(selectedQuote.lastPrice?.toString() || "0") * parseFloat(quantity || "0")
    : 0;

  const buyingPower = accounts?.[0]?.buyingPower
    ? parseFloat(accounts[0].buyingPower.toString())
    : 0;

  const filteredQuotes = quotes?.filter(
    (q) =>
      q.symbol?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      q.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const underlyingSymbols = ["AAPL", "TSLA", "MSFT", "GOOGL", "NVDA"];
  const underlyingPrice = quotes?.find((q) => q.symbol === optionsUnderlying)?.lastPrice || 0;

  const handlePlaceOrder = (side: "Buy" | "Sell") => {
    if (!selectedSymbol || !quantity) {
      toast({
        title: "Missing Information",
        description: "Please select a symbol and enter quantity.",
        variant: "destructive",
      });
      return;
    }

    placeOrderMutation.mutate({
      symbol: selectedSymbol,
      quantity: parseFloat(quantity),
      side,
      orderType,
      limitPrice: orderType === "Limit" ? parseFloat(limitPrice) : undefined,
      brokerage: selectedBrokerage,
    });
  };

  const handleCryptoOrder = (data: { symbol: string; quantity: number; side: string; orderType: string }) => {
    placeOrderMutation.mutate(data);
  };

  return (
    <MainLayout
      title="Trading"
      subtitle="Trade stocks, options, and crypto"
      actions={
        <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="button-refresh-quotes">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      }
    >
      <Tabs value={assetTab} onValueChange={setAssetTab} className="space-y-6">
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="stocks" className="gap-2" data-testid="tab-stocks">
            <TrendingUp className="h-4 w-4" />
            Stocks
          </TabsTrigger>
          <TabsTrigger value="options" className="gap-2" data-testid="tab-options">
            <BarChart3 className="h-4 w-4" />
            Options
          </TabsTrigger>
          <TabsTrigger value="crypto" className="gap-2" data-testid="tab-crypto">
            <Bitcoin className="h-4 w-4" />
            Crypto
          </TabsTrigger>
        </TabsList>

        <TabsContent value="stocks" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 gap-2">
                  <CardTitle className="text-lg font-medium">Market Watch</CardTitle>
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search symbols..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                      data-testid="input-search-symbols"
                    />
                  </div>
                </CardHeader>
                <CardContent>
                  {quotesLoading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[1, 2, 3, 4, 5, 6].map((i) => (
                        <Skeleton key={i} className="h-24 rounded-lg" />
                      ))}
                    </div>
                  ) : filteredQuotes && filteredQuotes.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {filteredQuotes.map((quote) => (
                        <QuoteCard
                          key={quote.symbol}
                          quote={quote}
                          onSelect={setSelectedSymbol}
                          assetClass="stock"
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-muted-foreground">
                      No quotes available
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6">
              <Card>
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg font-medium">Stock Order</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Symbol</Label>
                    <Select value={selectedSymbol} onValueChange={setSelectedSymbol}>
                      <SelectTrigger data-testid="select-symbol">
                        <SelectValue placeholder="Select symbol" />
                      </SelectTrigger>
                      <SelectContent>
                        {quotes?.map((quote) => (
                          <SelectItem key={quote.symbol} value={quote.symbol}>
                            {quote.symbol} - {quote.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedQuote && (
                    <div className="p-3 rounded-md bg-muted/50 space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Current Price</span>
                        <span className="font-bold">{formatCurrency(selectedQuote.lastPrice)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Change</span>
                        <span
                          className={`flex items-center gap-1 ${
                            parseFloat(selectedQuote.change?.toString() || "0") >= 0
                              ? "text-chart-1"
                              : "text-destructive"
                          }`}
                        >
                          {parseFloat(selectedQuote.change?.toString() || "0") >= 0 ? (
                            <ArrowUpRight className="h-4 w-4" />
                          ) : (
                            <ArrowDownRight className="h-4 w-4" />
                          )}
                          {formatPercent(selectedQuote.changePercent)}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Quantity</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      min="1"
                      data-testid="input-quantity"
                    />
                  </div>

                  <Tabs value={orderType} onValueChange={setOrderType} className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="Market">Market</TabsTrigger>
                      <TabsTrigger value="Limit">Limit</TabsTrigger>
                    </TabsList>
                    <TabsContent value="Limit" className="mt-4">
                      <div className="space-y-2">
                        <Label>Limit Price</Label>
                        <Input
                          type="number"
                          placeholder="0.00"
                          value={limitPrice}
                          onChange={(e) => setLimitPrice(e.target.value)}
                          step="0.01"
                          min="0"
                          data-testid="input-limit-price"
                        />
                      </div>
                    </TabsContent>
                  </Tabs>

                  <div className="space-y-2">
                    <Label>Execute via</Label>
                    <Select value={selectedBrokerage} onValueChange={setSelectedBrokerage}>
                      <SelectTrigger data-testid="select-brokerage">
                        <SelectValue placeholder="Select brokerage" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="paper">Paper Trading (Simulated)</SelectItem>
                        <SelectItem value="alpaca" disabled={!alpacaConfigured}>
                          <span className="flex items-center gap-2">
                            Alpaca {alpacaConfigured ? "(Connected)" : "(Not Configured)"}
                          </span>
                        </SelectItem>
                        <SelectItem value="tradier" disabled={!tradierConfigured}>
                          <span className="flex items-center gap-2">
                            Tradier {tradierConfigured ? "(Connected)" : "(Not Configured)"}
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    {selectedBrokerage !== "paper" && (
                      <div className="flex items-center gap-2 text-xs text-green-600">
                        <CheckCircle2 className="h-3 w-3" />
                        Orders will execute on real brokerage
                      </div>
                    )}
                    {selectedBrokerage === "paper" && (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <AlertCircle className="h-3 w-3" />
                        Simulated trading - no real orders
                      </div>
                    )}
                  </div>

                  <div className="space-y-2 pt-4 border-t">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Estimated Cost</span>
                      <span className="font-bold">{formatCurrency(estimatedCost)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Buying Power</span>
                      <span>{formatCurrency(buyingPower)}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 pt-2">
                    <Button
                      variant="default"
                      onClick={() => handlePlaceOrder("Buy")}
                      disabled={placeOrderMutation.isPending || !selectedSymbol || !quantity}
                      data-testid="button-buy"
                    >
                      {placeOrderMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        "Buy"
                      )}
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => handlePlaceOrder("Sell")}
                      disabled={placeOrderMutation.isPending || !selectedSymbol || !quantity}
                      data-testid="button-sell"
                    >
                      {placeOrderMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        "Sell"
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="options" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 gap-2">
                  <CardTitle className="text-lg font-medium">Options Chain</CardTitle>
                  <Select value={optionsUnderlying} onValueChange={setOptionsUnderlying}>
                    <SelectTrigger className="w-40" data-testid="select-underlying">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {underlyingSymbols.map((sym) => (
                        <SelectItem key={sym} value={sym}>{sym}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </CardHeader>
                <CardContent>
                  <OptionsChain
                    underlyingSymbol={optionsUnderlying}
                    underlyingPrice={parseFloat(underlyingPrice?.toString() || "0")}
                  />
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6">
              <Card>
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg font-medium">Options Order</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 rounded-md bg-purple-500/10 text-center">
                    <BarChart3 className="h-8 w-8 mx-auto text-purple-500 mb-2" />
                    <p className="text-sm text-muted-foreground">
                      Select an option from the chain to place an order
                    </p>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    <p className="font-medium mb-2">Options Trading Features:</p>
                    <ul className="space-y-1 list-disc list-inside">
                      <li>Real-time options chain data</li>
                      <li>Greeks (Delta, Gamma, Theta, Vega)</li>
                      <li>Multi-leg strategies</li>
                      <li>Risk analysis tools</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="crypto" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 gap-2">
                  <CardTitle className="text-lg font-medium">Crypto Market</CardTitle>
                  <Badge variant="outline" className="text-orange-500 border-orange-500">
                    <Coins className="h-3 w-3 mr-1" />
                    24/7 Trading
                  </Badge>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {instruments?.filter((i) => i.assetClass === "crypto").map((crypto) => (
                      <Card key={crypto.symbol} className="hover-elevate cursor-pointer" data-testid={`crypto-card-${crypto.symbol}`}>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className="h-8 w-8 rounded-md bg-orange-500/10 flex items-center justify-center">
                                <Bitcoin className="h-4 w-4 text-orange-500" />
                              </div>
                              <div>
                                <p className="font-medium">{crypto.baseCurrency}</p>
                                <p className="text-xs text-muted-foreground">{crypto.name}</p>
                              </div>
                            </div>
                            <Badge variant="default" className="text-xs bg-chart-1">
                              <TrendingUp className="h-3 w-3 mr-1" />
                              +2.45%
                            </Badge>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xl font-bold">
                              {formatCurrency(
                                crypto.symbol === "BTC-USD" ? 97250 :
                                crypto.symbol === "ETH-USD" ? 3450 :
                                crypto.symbol === "SOL-USD" ? 145.50 :
                                crypto.symbol === "DOGE-USD" ? 0.082 :
                                crypto.symbol === "XRP-USD" ? 0.62 : 0
                              )}
                            </span>
                            <span className="text-sm text-chart-1">+$1,234.56</span>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6">
              <Card>
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg font-medium flex items-center gap-2">
                    <Bitcoin className="h-5 w-5 text-orange-500" />
                    Crypto Order
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CryptoOrderTicket
                    instruments={instruments || []}
                    onOrder={handleCryptoOrder}
                    isPending={placeOrderMutation.isPending}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="text-xs text-muted-foreground">
                    <p className="font-medium mb-2">Crypto Trading Info:</p>
                    <ul className="space-y-1 list-disc list-inside">
                      <li>Trade 24/7, 365 days a year</li>
                      <li>No minimum investment</li>
                      <li>Instant execution</li>
                      <li>0.5% trading fee</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </MainLayout>
  );
}
